import {
  ADDRESS_MISSING_CITY,
  ADDRESS_MISSING_STREET,
  ADDRESS_MISSING_STREET_NUMBER,
  getAddressDetails,
  getAddressFormatted,
  isAddressValid,
  isQrCode,
  isStringNotNull,
  parseAddress,
  renderErrorMessage,
} from '../../utils';
import {
  DEFAULT_TEXT,
  MISSING_STREET_NUMBER,
  NO_SHOP_AVAILABLE_ERROR_MESSAGE,
  NO_SHOP_SELECTED_ERROR_MESSAGE,
  NOT_FOUND_INDEX,
  ORDER_TYPE_DELIVERY,
  PLACEHOLDER_TEXT,
  UNAVAILABLE_ADDRESS,
  WRONG_ADDRESS,
} from '../../constants';
import { useAppDispatch, useAppSelector } from '../../redux/hook';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  showAddressModal,
  updateClosestShops,
  updateCustomerAddressCheck,
  showMessageModal,
  updateShopIsLoaded,
} from '../../redux/actions';
import {
  isCustomerAddressDefined,
  updateUserAddressAndOrderType,
} from '../../utils/customer-address-and-shops';
import type { Address } from '../../1_types/genericTypes';
import { getAvailableShops } from '../../api/delivery';
import { CUSTOMER_ADDRESS_CHECK_VALUES } from '../../redux/reducers/componentReducer';
import {
  analyticsEvents,
  PAGES,
  PROVENANCE,
} from '../../utils/firebaseAnalytics';
import _ from 'lodash';
import { getGeocodeByAddress, getGeocodeByPlaceId } from '../../api';
import store from '../../redux/store';
import { logMediumError } from '../../utils/logger';

const DEBOUNCE_TIME = 200;

const fetchAvailableShops = async (
  orderType: string,
  address: Address | null | undefined,
  handleCatchError?: (error: unknown) => Promise<void>,
): Promise<any[]> => {
  try {
    const shops = await getAvailableShops(orderType, address);
    return (shops ?? []).map((shop: any) => ({
      ...shop,
      shopId: shop.id ?? shop.shopId,
    }));
  } catch (error) {
    if (handleCatchError) {
      await handleCatchError(error);
    }
    return [];
  }
};

export const useAddressInputModal = (
  resetMode = false,
  shopIdFromLink: string | null = null,
) => {
  const dispatch = useAppDispatch();

  const {
    secondaryColor,
    orderTypes: defaultShopOrderTypes,
    qrcode,
  } = useAppSelector((state) => state.configurationReducer);
  const { closestShops } = useAppSelector((state) => state.shopReducer as any);
  let { address: customerAddress } = useAppSelector(
    (state) => state.pendingOrderReducer,
  );
  const { orderType: pendingOrderOrderType, shopId: pendingOrderShopId } =
    useAppSelector((state) => state.pendingOrderReducer);

  if (resetMode) {
    customerAddress = null;
  }
  const orderTypes = isQrCode()
    ? _.filter(qrcode?.orderTypes, (type) => type !== 'delivery')
    : defaultShopOrderTypes;
  const { isMobile, googleMapsEnabled, addressModal } = useAppSelector(
    (state) => state.componentReducer,
  );
  const [isCustomerAddress, setIsCustomerAddress] = useState(
    isCustomerAddressDefined(customerAddress),
  );
  const [typedAddress, setTypedAddress] = useState('');
  const [lastTypedAddressValidValue, setLastTypedAddressValidValue] =
    useState('');
  const defaultShopId = resetMode
    ? shopIdFromLink || null
    : (shopIdFromLink ?? pendingOrderShopId) || null;
  const [selectedShopId, setSelectedShopId] = useState(defaultShopId);
  const [selectedAddressPlaceId, setSelectedAddressPlaceId] = useState(null);
  const [getShopsIsLoading, setGetShopsIsLoading] = useState(false);
  const [openShopsDropdownList, setOpenShopsDropdownList] = useState(null);
  const defaultOrderTypeIndex = orderTypes?.findIndex((type: string) => {
    const targetOrderType = pendingOrderOrderType;
    return type === targetOrderType;
  });
  const [orderTypeChoiceIndex, setOrderTypeChoiceIndex] = useState(
    defaultOrderTypeIndex !== NOT_FOUND_INDEX ? defaultOrderTypeIndex : 0,
  );
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [shopDropdowListText, setShopDropdowListText] = useState(DEFAULT_TEXT);
  const [isFirstTimeFetching, setIsFirstTimeFetching] = useState(true);
  const [displayIsSavingSpinner, setDisplayIsSavingSpinner] = useState(false);
  const isMountedRef = useRef(true);
  const inputElementRef = useRef(null);

  const orderType = orderTypes[orderTypeChoiceIndex];
  const isForcedNewAddressForDelivery =
    orderType === ORDER_TYPE_DELIVERY &&
    !!pendingOrderOrderType &&
    pendingOrderOrderType !== ORDER_TYPE_DELIVERY;
  const displayedSavedAddress = isForcedNewAddressForDelivery
    ? null
    : customerAddress;
  const selectedShop =
    selectedShopId &&
    closestShops?.find((shop: any) => shop?.shopId === selectedShopId);
  const hasErrorAddressMessage = [
    UNAVAILABLE_ADDRESS,
    MISSING_STREET_NUMBER,
    WRONG_ADDRESS,
    ADDRESS_MISSING_CITY,
    ADDRESS_MISSING_STREET,
    ADDRESS_MISSING_STREET_NUMBER,
  ].includes(errorMessage);
  const isFilledAddressValid = isCustomerAddress
    ? isCustomerAddressDefined(customerAddress)
    : !!selectedAddressPlaceId &&
    typedAddress === lastTypedAddressValidValue &&
    lastTypedAddressValidValue?.length &&
    !hasErrorAddressMessage;

  const showShopsDropdownListWhenOrderTypeDelivery = (
    isCutomerAddress: boolean,
    hasErrorOnAddress: boolean,
    orderType: string,
  ) =>
    orderType === ORDER_TYPE_DELIVERY &&
    (isCutomerAddress || (!isCutomerAddress && selectedAddressPlaceId)) &&
    (!hasErrorOnAddress || getShopsIsLoading);
  const showShopsDropdownList = (
    isCutomerAddressSelected = isCustomerAddress,
    hasErrorOnAddress = hasErrorAddressMessage,
    orderTypeSelected = orderType,
  ) =>
    showShopsDropdownListWhenOrderTypeDelivery(
      isCutomerAddressSelected,
      hasErrorOnAddress,
      orderTypeSelected,
    ) ||
    (orderTypeSelected !== ORDER_TYPE_DELIVERY && !!closestShops?.length);
  const shouldRenderAddressSelectionRadioButton =
    isCustomerAddressDefined(displayedSavedAddress) &&
    (orderType === ORDER_TYPE_DELIVERY ||
      (orderType !== ORDER_TYPE_DELIVERY && closestShops?.length > 1));
  const shouldRenderAddressSearchInput =
    ((orderType !== ORDER_TYPE_DELIVERY && closestShops?.length > 1) ||
      orderType === ORDER_TYPE_DELIVERY) &&
    googleMapsEnabled &&
    !isCustomerAddress;
  const animatePlaceholder =
    orderTypes[orderTypeChoiceIndex] === ORDER_TYPE_DELIVERY
      ? PLACEHOLDER_TEXT
      : `${PLACEHOLDER_TEXT}(facultatif)`;
  const shoulRenderSpinner = isFirstTimeFetching;

  const getAddressFromCustomerAddress = useCallback(
    async (customerAddress: any) => {
      if (!isCustomerAddressDefined(customerAddress)) {
        return [];
      }
      return customerAddress?.placeId
        ? getGeocodeByPlaceId(customerAddress.placeId)
        : getGeocodeByAddress(getAddressFormatted(customerAddress));
    },
    [customerAddress],
  );

  const fetchAdressData = useCallback(
    async (selectedAddressPlaceId: string, customerAddress: any) => {
      try {
        if (
          (customerAddress && !selectedAddressPlaceId) ||
          selectedAddressPlaceId === customerAddress?.placeId
        ) {
          setSelectedAddress(customerAddress);
          return customerAddress;
        }
        const foundPlaces = await (selectedAddressPlaceId
          ? getGeocodeByPlaceId(selectedAddressPlaceId)
          : getAddressFromCustomerAddress(customerAddress));
        const firstFoundPlace = foundPlaces?.length ? foundPlaces[0] : null;
        const foundSelectedAddress = firstFoundPlace
          ? parseAddress(firstFoundPlace?.address_components)
          : null;
        if (!foundSelectedAddress) {
          setSelectedAddress(null);
          return null;
        }
        const { lat, lng } = firstFoundPlace.geometry.location;
        const { district, subLocality } = getAddressDetails([firstFoundPlace]);
        foundSelectedAddress.district = district;
        foundSelectedAddress.subLocality = subLocality;
        foundSelectedAddress.location = {
          latitude: lat,
          longitude: lng,
        };
        foundSelectedAddress.placeId = firstFoundPlace?.place_id;
        if (!isMountedRef.current) {
          return;
        }
        setSelectedAddress(foundSelectedAddress);
        return foundSelectedAddress;
      } catch (error) {
        await handleCatchError(error);
      }
    },
    [orderType],
  );

  const getAvailableClosestShopsFromAddress = useCallback(
    async (orderType: string, selectedAddress: any) => {
      try {
        if (!isAddressValid(selectedAddress)) {
          return await getShopsIfInvalidAddress(orderType);
        }
        if (!isMountedRef.current) {
          return;
        }
        if (
          !isStringNotNull(selectedAddress.street) &&
          orderType === ORDER_TYPE_DELIVERY
        ) {
          setErrorMessage(ADDRESS_MISSING_STREET);
          setGetShopsIsLoading(false);
          return [];
        }
        if (
          !isStringNotNull(selectedAddress.streetNumber) &&
          orderType === ORDER_TYPE_DELIVERY
        ) {
          setErrorMessage(MISSING_STREET_NUMBER);
          setGetShopsIsLoading(false);
          return [];
        }
        if (orderType !== ORDER_TYPE_DELIVERY) {
          const shops = await fetchAvailableShops(
            orderType,
            selectedAddress,
            handleCatchError,
          );

          if (!shops?.length) {
            setErrorMessage(NO_SHOP_AVAILABLE_ERROR_MESSAGE);
            return [];
          }
          handleOnlyOneShopAvailable(shops);
          dispatch(updateClosestShops(shops));
          return shops;
        }
        setGetShopsIsLoading(true);
        const deliverableShops = await fetchAvailableShops(
          ORDER_TYPE_DELIVERY,
          selectedAddress,
          handleCatchError,
        );
        if (!isMountedRef.current) {
          return;
        }
        if (!deliverableShops?.length) {
          setErrorMessage(UNAVAILABLE_ADDRESS);
          setGetShopsIsLoading(false);
          return [];
        }
        handleSelectedShopForDelivery(deliverableShops);
        dispatch(updateClosestShops(deliverableShops));
        setGetShopsIsLoading(false);
        return deliverableShops;
      } catch (error) {
        await handleCatchError(error);
        setGetShopsIsLoading(false);
      }
    },
    [
      customerAddress,
      orderTypeChoiceIndex,
      selectedAddressPlaceId,
      isCustomerAddress,
      errorMessage,
    ],
  );

  const getShopsIfInvalidAddress = useCallback(async (orderType: string) => {
    if (orderType !== ORDER_TYPE_DELIVERY) {
      const shops = await fetchAvailableShops(
        orderType,
        null,
        handleCatchError,
      );
      dispatch(updateClosestShops(shops));
      setErrorMessage(shops?.length ? null : NO_SHOP_AVAILABLE_ERROR_MESSAGE);
      if (shops?.length === 1) {
        setSelectedShopId(shops[0]?.shopId ?? defaultShopId);
      }
      return shops;
    }
  }, []);

  const handleValidationButtonClick = useCallback(async () => {
    const {
      user: { customerId },
    } = store.getState().userReducer;

    if (getShopsIsLoading || displayIsSavingSpinner) {
      return;
    }
    if (
      orderType === ORDER_TYPE_DELIVERY &&
      !isCustomerAddress &&
      !selectedAddressPlaceId
    ) {
      setErrorMessage(WRONG_ADDRESS);
      return;
    }

    if (!selectedShop) {
      setErrorMessage(NO_SHOP_SELECTED_ERROR_MESSAGE);
      return;
    }
    try {
      await validationButtonClick();
    } catch (e) {
      logMediumError(
        `Failed to update address and shop for customer (${customerId}) ${e}`,
      );
    }
    setDisplayIsSavingSpinner(false);
  }, [
    orderTypeChoiceIndex,
    selectedShopId,
    selectedAddressPlaceId,
    closestShops,
    getShopsIsLoading,
  ]);

  const validSelectedAddress = useCallback(() => {
    if (!selectedAddress) {
      return '';
    }

    if (!isStringNotNull(selectedAddress.city)) {
      return renderErrorMessage('address/missing-city');
    } else if (!isStringNotNull(selectedAddress.street) && orderType === ORDER_TYPE_DELIVERY) {
      return renderErrorMessage('address/missing-street');
    }
    return '';
  }, [selectedAddress]);

  const validationButtonClick = useCallback(async () => {
    setDisplayIsSavingSpinner(true);
    const addressErrorMessage = validSelectedAddress();
    if (isStringNotNull(addressErrorMessage)) {
      dispatch(
        updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.INVALID),
      );
      setErrorMessage(addressErrorMessage);
      return;
    }

    const customerNewAddress =
      orderType === ORDER_TYPE_DELIVERY
        ? isForcedNewAddressForDelivery
          ? selectedAddress
          : selectedAddress || customerAddress || selectedShop?.address
        : (!isCustomerAddress && selectedAddressPlaceId && isStringNotNull(selectedAddress?.street) && selectedAddress) ||
        customerAddress ||
        selectedShop?.address;
    dispatch(updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.VALID));
    const shopId = selectedShopId;

    dispatch(updateShopIsLoaded(false));
    const message = await updateUserAddressAndOrderType(
      shopId,
      customerNewAddress,
      orderType,
    );
    setDisplayIsSavingSpinner(false);
    dispatch(showAddressModal(false));
    if (message) store.dispatch(showMessageModal(message));
    analyticsEvents.logShopSelectionConfirmed(selectedShopId);
    analyticsEvents.logPageViewEvent(
      PAGES.SALES_MODE,
      PROVENANCE.SALES_MODE_BUTTON,
    );
  }, [
    orderTypeChoiceIndex,
    selectedShopId,
    selectedAddressPlaceId,
    closestShops,
    getShopsIsLoading,
  ]);

  const handleAnimatePlaceHolderText = useCallback(() => {
    let str = '';
    let n = 0;
    const animate = () => {
      if (inputElementRef.current) {
        if (n < animatePlaceholder.length) {
          str += animatePlaceholder[n];
          inputElementRef.current.placeholder = str;
          n += 1;
        } else {
          str = '';
          n = 0;
          inputElementRef.current.placeholder = str;
          setTimeout(animate, DEBOUNCE_TIME * 5);
          return;
        }
      }
    };
    const interval = setInterval(animate, DEBOUNCE_TIME);
    return () => clearInterval(interval);
  }, [orderTypeChoiceIndex]);

  useEffect(() => {
    if (getShopsIsLoading) {
      let dots = '';
      const animateDots = () => {
        if (dots.length <= 3) {
          setShopDropdowListText(`Recherche en cours${dots}`);
          dots += '.';
        } else {
          dots = '';
        }
      };
      const debounceTimer = setInterval(animateDots, DEBOUNCE_TIME);
      return () => clearTimeout(debounceTimer);
    } else {
      setShopDropdowListText(selectedShop?.name || DEFAULT_TEXT);
    }
  }, [getShopsIsLoading, selectedShop]);

  useEffect(() => {
    const fetchData = async () => {
      if (isCustomerAddress) {
        fetchAdressData(selectedAddressPlaceId, customerAddress)
          .then((initialAddress: any) =>
            getAvailableClosestShopsFromAddress(orderType, initialAddress),
          )
          .then((availableShops: any[]) => {
            if (!isMountedRef.current) {
              return;
            }
            setIsFirstTimeFetching(false);
            const availableShopsHasPendingOrderShopId = !!availableShops?.find(
              (shop) => shop?.shopId === pendingOrderShopId,
            );
            if (!availableShopsHasPendingOrderShopId) {
              setSelectedShopId(defaultShopId);
            }
          },
          );
      } else {
        const fetchShops =
          orderType !== ORDER_TYPE_DELIVERY
            ? getAvailableClosestShopsFromAddress(orderType, null)
            : Promise.resolve([]);
        fetchShops.then((availableShops: any[]) => {
          if (availableShops?.length > 1) {
            setSelectedShopId(defaultShopId);
          }
          setIsFirstTimeFetching(false);
        });
      }
    };
    isMountedRef.current = true;
    fetchData();
    return () => {
      isMountedRef.current = false;
    };
  }, [customerAddress, pendingOrderShopId]);

  useEffect(() => {
    const fetchData = async () => {
      const noAddressCandidateSelected =
        !isCustomerAddress && !selectedAddressPlaceId;
      const noSearchContextAvailable =
        !isCustomerAddress &&
        !closestShops?.length &&
        !lastTypedAddressValidValue?.length;

      if (
        noAddressCandidateSelected ||
        noSearchContextAvailable ||
        (!isMountedRef.current && !!closestShops?.length) ||
        isFirstTimeFetching
      ) {
        return;
      }
      fetchAdressData(selectedAddressPlaceId, customerAddress).then(
        async (foundAddress: any) =>
          getAvailableClosestShopsFromAddress(orderType, foundAddress).then(
            (availableClosestShops: any[]) => {
              if (!isMountedRef.current) {
                return;
              }
              const closestShopsHasPendingOrderShopId =
                !!availableClosestShops?.find(
                  (shop) => shop?.shopId === pendingOrderShopId,
                );
              if (
                isCustomerAddress &&
                (orderType !== ORDER_TYPE_DELIVERY ||
                  (orderType === ORDER_TYPE_DELIVERY &&
                    showShopsDropdownList(
                      isCustomerAddress,
                      hasErrorAddressMessage,
                      orderType,
                    ))) &&
                closestShopsHasPendingOrderShopId
              ) {
                setSelectedShopId(pendingOrderShopId);
              } else if (
                orderType === ORDER_TYPE_DELIVERY &&
                !showShopsDropdownList(
                  isCustomerAddress,
                  hasErrorAddressMessage,
                  orderType,
                )
              ) {
                setSelectedShopId(availableClosestShops?.[0]?.shopId);
              } else {
                const shopId =
                  availableClosestShops?.length === 1
                    ? availableClosestShops[0]?.shopId
                    : defaultShopId;
                setSelectedShopId(shopId);
              }
            },
          ),
      );
    };
    isMountedRef.current = true;
    fetchData();
    return () => {
      isMountedRef.current = false;
    };
  }, [
    customerAddress,
    selectedAddressPlaceId,
    isCustomerAddress,
    orderTypeChoiceIndex,
  ]);

  useEffect(() => {
    if (isForcedNewAddressForDelivery) {
      setIsCustomerAddress(false);
    }
  }, [isForcedNewAddressForDelivery]);

  useEffect(() => {
    if (isForcedNewAddressForDelivery && !isCustomerAddress) {
      inputElementRef.current?.focus();
    }
  }, [isForcedNewAddressForDelivery, isCustomerAddress]);

  useEffect(() => {
    return handleAnimatePlaceHolderText();
  }, [orderTypeChoiceIndex]);

  const handleCatchError = async (error: any) => {
    const errorMessage = String(error);
    if (errorMessage) {
      if (
        !errorMessage.includes('déployé sur') &&
        !errorMessage.includes('incorrecte')
      ) {
        logMediumError(`Could not add address: ${errorMessage}`);
      }
      if (errorMessage.includes('ZERO_RESULTS')) {
        setErrorMessage(UNAVAILABLE_ADDRESS);
      }
      if (errorMessage.includes('CANT_GEOCODE_ADDRESS')) {
        setErrorMessage(renderErrorMessage('stuart/address-not-served'));
      }
      if (
        !errorMessage?.includes('INVALID_REQUEST') &&
        !errorMessage?.includes('ZERO_RESULTS')
      ) {
        logMediumError(`Could not get address ${error}`);
      }
    } else {
      setErrorMessage(null);
    }
  };

  const handleOnlyOneShopAvailable = (shops: any[]): void => {
    if (shops?.length === 1) {
      setSelectedShopId(shops[0]?.shopId);
    }
  };

  const handleSelectedShopForDelivery = (shops: any[]): void => {
    if (shops?.length && !showShopsDropdownList() && !isCustomerAddress) {
      setSelectedShopId(shops[0]?.shopId);
    }
  };

  const handleShopSelection = (shopId: string) => {
    if (!shopId) {
      return;
    }
    setSelectedShopId(shopId);
    setOpenShopsDropdownList(false);
    setErrorMessage(null);
    analyticsEvents.logShopSelection(shopId);
  };

  const handleAddressTypeSelection = async ({ target: target }) => {
    const isCustomerAddressChoosen = target.value === 'true';
    setIsCustomerAddress(isCustomerAddressChoosen);
    setErrorMessage(null);
    if (isCustomerAddressChoosen) {
      setSelectedAddressPlaceId(null);
      setTypedAddress('');
      const isOrderTypeDeliveryAndShopsDropdownListHidden =
        orderType === ORDER_TYPE_DELIVERY &&
        !showShopsDropdownList(isCustomerAddressChoosen, false);
      setSelectedShopId(
        (!isOrderTypeDeliveryAndShopsDropdownListHidden &&
          pendingOrderShopId) ||
        (closestShops?.length && closestShops[0]?.shopId) ||
        defaultShopId,
      );
      analyticsEvents.logAddressSelection(selectedAddress);
      if (orderType === ORDER_TYPE_DELIVERY) {
        setOpenShopsDropdownList(null);
      }
    } else {
      setSelectedShopId(defaultShopId);
      if (orderType !== ORDER_TYPE_DELIVERY) {
        await getAvailableClosestShopsFromAddress(orderType, selectedAddress);
      } else {
        setOpenShopsDropdownList(null);
      }
    }
  };

  const handleOrderTypeSelection = async (selectedOrderTypeIndex: number) => {
    if (getShopsIsLoading) {
      return;
    }
    const selectedOrderType = orderTypes[selectedOrderTypeIndex];
    analyticsEvents.logSelectSaleMode(selectedOrderType);
    setOrderTypeChoiceIndex(selectedOrderTypeIndex);
    setOpenShopsDropdownList(null);
    setErrorMessage(null);
    if (
      (typedAddress !== lastTypedAddressValidValue &&
        lastTypedAddressValidValue?.length) ||
      !lastTypedAddressValidValue?.length
    ) {
      setSelectedAddressPlaceId(null);
      setTypedAddress('');
      setLastTypedAddressValidValue('');
    }
    const willEnterForcedNewAddressForDelivery =
      selectedOrderType === ORDER_TYPE_DELIVERY &&
      !!pendingOrderOrderType &&
      pendingOrderOrderType !== ORDER_TYPE_DELIVERY;
    if (willEnterForcedNewAddressForDelivery) {
      setSelectedAddress(null);
      setSelectedAddressPlaceId(null);
      setTypedAddress('');
      setLastTypedAddressValidValue('');
      dispatch(updateClosestShops([]));
    }
    if (selectedOrderType === ORDER_TYPE_DELIVERY) {
      return;
    }
    const referenceAddress = isCustomerAddress
      ? customerAddress
      : selectedAddress;
    const availableClosestShops = await getAvailableClosestShopsFromAddress(
      selectedOrderType,
      referenceAddress,
    );
    const closestShopsHasPendingOrderShopId = !!availableClosestShops?.find(
      (shop) => shop?.shopId === pendingOrderShopId,
    );
    if (isCustomerAddress && closestShopsHasPendingOrderShopId) {
      setSelectedShopId(pendingOrderShopId);
    } else {
      const shopId =
        availableClosestShops?.length === 1
          ? availableClosestShops[0]?.shopId
          : defaultShopId;
      setSelectedShopId(shopId);
    }
  };

  const handleAddressSelection = async (
    selectedAddressValue: any,
    placeId: string,
  ) => {
    setTypedAddress(selectedAddressValue);
    setLastTypedAddressValidValue(selectedAddressValue);
    setSelectedAddressPlaceId(placeId);
    setSelectedShopId(defaultShopId);
    setOpenShopsDropdownList(null);
  };

  const handleAddressInputFormControlError = (
    status: any,
    clearSuggestions: () => void,
  ) => {
    const errorMessage = String(status);
    if (!errorMessage.includes('ZERO_RESULTS')) {
      logMediumError(`Error from google api ${status}`);
    }
    setErrorMessage(WRONG_ADDRESS);
    clearSuggestions();
  };

  const handleShopsDropdownListClick = () => {
    if (!getShopsIsLoading) {
      setOpenShopsDropdownList(!openShopsDropdownList);
      if (!openShopsDropdownList) {
        setErrorMessage(null);
      }
    }
  };

  useEffect(() => {
    setErrorMessage(null);
  }, [typedAddress]);

  return {
    showShopsDropdownList,
    handleAddressTypeSelection,
    handleOrderTypeSelection,
    handleAddressSelection,
    setTypedAddress,
    handleAddressInputFormControlError,
    setOpenShopsDropdownList,
    handleShopsDropdownListClick,
    handleShopSelection,
    setErrorMessage,
    errorMessage,
    isMobile,
    addressModal,
    closestShops,
    isCustomerAddress,
    customerAddress,
    displayedSavedAddress,
    secondaryColor,
    orderTypes,
    orderTypeChoiceIndex,
    typedAddress,
    handleValidationButtonClick,
    selectedShop,
    isFilledAddressValid,
    openShopsDropdownList,
    shouldRenderAddressSelectionRadioButton,
    isForcedNewAddressForDelivery,
    selectedAddress,
    orderType,
    hasErrorAddressMessage,
    shouldRenderAddressSearchInput,
    getShopsIsLoading,
    animatePlaceholder,
    inputElementRef,
    shopDropdowListText,
    shoulRenderSpinner,
    isFirstTimeFetching,
    displayIsSavingSpinner,
    validationButtonClick,
    lastTypedAddressValidValue,
  };
};
